import {
  EmployerSignupStatus,
  EmployerStatus,
  PayrollCycle,
  WaitingPeriod,
} from '@zorro/clients';
import { APP_ROUTES } from '@zorro/common-utils';
import { StrictExtract } from '@zorro/types';
import { $enum } from 'ts-enum-util';

import { exportToCsv } from './exportToCsv';

export type EmployerStatusRoutes = StrictExtract<
  typeof APP_ROUTES,
  | 'OPS_COMPANY_SETUP_PROSPECT'
  | 'OPS_ADD_EMPLOYER_COMPANY_DETAILS'
  | 'OPS_ADD_EMPLOYER_REVIEW'
  | 'OPS_EMPLOYERS_OVERVIEW'
  | 'ERROR'
>;

export const getRouteForEmployerStatus = (
  employerStatus: EmployerStatus | EmployerSignupStatus
): EmployerStatusRoutes => {
  return $enum.mapValue(employerStatus).with<EmployerStatusRoutes>({
    [EmployerStatus.PROSPECT]: 'OPS_COMPANY_SETUP_PROSPECT',
    [EmployerStatus.DRAFT]: 'OPS_ADD_EMPLOYER_COMPANY_DETAILS',
    [EmployerStatus.WAITING_FOR_EMPLOYER]: 'OPS_ADD_EMPLOYER_REVIEW',
    [EmployerStatus.NEEDS_REVIEW]: 'OPS_ADD_EMPLOYER_REVIEW',
    [EmployerStatus.IN_ZORRO_SETUP]: 'OPS_ADD_EMPLOYER_REVIEW',
    [EmployerStatus.ACTIVE]: 'OPS_EMPLOYERS_OVERVIEW',
    [EmployerStatus.DEACTIVATED]: 'OPS_EMPLOYERS_OVERVIEW',
    [$enum.handleUnexpected]: 'ERROR',
  });
};

export type EmployerRow = {
  id: string;
  name: string;
  status: EmployerStatus;
  coverageStart: string;
  openEnrollmentStart: string;
  openEnrollmentEnd: string;
  totalEmployees: number;
  enrolledEmployees: number;
  enrollmentTeams: string[];
  zorroPartner: string | null;
  zorroPartnerId: string | null;
  producer: string | null;
  producerAgent: string | null;
  csm: string | null;
  estimatedEmployees: number;
  estimatedParticipationRate: number;
  state?: string | null;
  payrollCycle: PayrollCycle | string;
  waitingPeriod: WaitingPeriod | string;
};

export function downloadEmployers(records: EmployerRow[]) {
  const fileName = `zorro_employer_${Date.now()}.csv`;

  const recordsToExport = records.map(({ id, zorroPartnerId, ...rest }) => ({
    ...rest,
    enrollmentTeams: rest.enrollmentTeams.join(', '),
  }));
  const columns = [
    { value: 'name', label: 'Name' },
    { value: 'producerAgent', label: 'Producer agent' },
    { value: 'producer', label: 'Producer' },
    { value: 'csm', label: 'CSM' },
    { value: 'enrollmentTeams', label: 'Enrollment teams' },
    { value: 'zorroPartner', label: 'Zorro partner' },
    { value: 'status', label: 'Status' },
    { value: 'coverageStart', label: 'Coverage start' },
    { value: 'openEnrollmentStart', label: 'Open enrollment start' },
    { value: 'openEnrollmentEnd', label: 'Open enrollment end' },
    { value: 'totalEmployees', label: 'Total employees' },
    { value: 'enrolledEmployees', label: 'Enrolled employees' },
    { value: 'estimatedEmployees', label: 'Estimated employees' },
    {
      value: 'estimatedParticipationRate',
      label: 'Estimated participation rate',
    },
    { value: 'state', label: 'State' },
    { value: 'payrollCycle', label: 'Payroll cycle' },
    { value: 'waitingPeriod', label: 'Waiting period' },
  ];

  exportToCsv(recordsToExport, fileName, columns);
}
